import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import type { Company, Facility } from './case/types';
import {
  FINANCING_SOURCES,
  INSTRUMENT_TYPES,
  LOAN_TYPES,
  fmtMoney,
  fmtRate,
  isLoanInstrument,
  labelOf,
} from './case/types';

/**
 * CreditFacilitiesPage — standalone, deep-edit screen for credit facilities.
 *
 * Routed at `/business/cases/:caseId/facilities`. This is the screen the
 * advisor opens when they need the full facility editor: every instrument
 * type, financing source, loan-shape, and rate/term/maturities at once.
 *
 * For the day-to-day "add a few rows during case review" flow, see the
 * CreditPortfolioTab inside CaseDetailPage. Both views read the same
 * `/facilities` endpoint and share types/constants from `./case/types.ts`.
 */

type FacilityFormState = {
  company_id: string;
  instrument_type: string;
  loan_type: string;
  financing_source_type: string;
  bank_name: string;
  facility_label: string;
  amount_current: string;
  annual_rate_percent: string;
  term_months: string;
  current_annual_maturities: string;
  notes: string;
};

const emptyForm: FacilityFormState = {
  company_id: '',
  instrument_type: 'long_term_loan',
  loan_type: 'SHPITZER',
  financing_source_type: 'commercial_bank',
  bank_name: '',
  facility_label: '',
  amount_current: '',
  annual_rate_percent: '',
  term_months: '',
  current_annual_maturities: '',
  notes: '',
};

export function CreditFacilitiesPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<FacilityFormState>(emptyForm);

  const companiesQuery = useQuery<Company[]>({
    queryKey: ['companies', caseId],
    queryFn: () => apiClient.get<Company[]>(`/companies?case_id=${caseId}`),
    enabled: !!caseId,
  });

  const facilitiesQuery = useQuery<Facility[]>({
    queryKey: ['facilities', 'case', caseId],
    queryFn: () => apiClient.get<Facility[]>(`/facilities?case_id=${caseId}`),
    enabled: !!caseId,
  });

  const companyMap = useMemo(() => {
    const m = new Map<string, Company>();
    for (const c of companiesQuery.data ?? []) m.set(c.id, c);
    return m;
  }, [companiesQuery.data]);

  const createFacility = useMutation<Facility, Error, FacilityFormState>({
    mutationFn: (state) => {
      const body: Record<string, unknown> = {
        company_id: state.company_id,
        instrument_type: state.instrument_type,
        financing_source_type: state.financing_source_type,
      };
      if (isLoanInstrument(state.instrument_type) && state.loan_type) {
        body.loan_type = state.loan_type;
      }
      if (state.bank_name) body.bank_name = state.bank_name;
      if (state.facility_label) body.facility_label = state.facility_label;
      if (state.amount_current) body.amount_current = state.amount_current;
      if (state.annual_rate_percent) {
        body.annual_rate = (
          Number(state.annual_rate_percent) / 100
        ).toString();
      }
      if (state.term_months) body.term_months = Number(state.term_months);
      if (state.current_annual_maturities) {
        body.current_annual_maturities = state.current_annual_maturities;
      }
      if (state.notes) body.notes = state.notes;
      return apiClient.post<Facility>('/facilities', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities', 'case', caseId] });
      setForm(emptyForm);
      setIsAdding(false);
    },
  });

  const deleteFacility = useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/facilities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities', 'case', caseId] });
    },
  });

  const openAdd = () => {
    const firstCompany = companiesQuery.data?.[0];
    setForm({
      ...emptyForm,
      company_id: firstCompany?.id ?? '',
    });
    setIsAdding(true);
  };

  const canSubmit =
    !!form.company_id &&
    !!form.instrument_type &&
    !!form.financing_source_type &&
    !createFacility.isPending;

  return (
    <div>
      <div className="flex items-center gap-3 mb-2 text-sm text-slate-500">
        <Link
          to={`/business/cases/${caseId}`}
          className="inline-flex items-center gap-1 hover:text-business-primary"
        >
          <ArrowRight className="w-4 h-4" />
          חזרה לקייס
        </Link>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-navy mb-1">תיק אשראי קיים</h1>
          <p className="text-slate-600">
            הזנת הלוואות, מסגרות אשראי וכלי חוב נוספים של החברות בקייס
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          disabled={
            isAdding || !companiesQuery.data || companiesQuery.data.length === 0
          }
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          הוסף הלוואה
        </button>
      </div>

      {(companiesQuery.data?.length ?? 0) === 0 && companiesQuery.isFetched && (
        <div className="card mb-6 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border-amber-200">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            עוד לא הוזנו חברות בקייס. הוסף לפחות חברה אחת לפני הזנת הלוואות.
          </span>
        </div>
      )}

      {isAdding && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-navy mb-4">הלוואה חדשה</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="חברה">
              <select
                value={form.company_id}
                onChange={(e) =>
                  setForm({ ...form, company_id: e.target.value })
                }
                className="input"
              >
                <option value="">— בחר חברה —</option>
                {(companiesQuery.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.short_label}
                    {c.is_primary ? ' ★' : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="סוג מכשיר">
              <select
                value={form.instrument_type}
                onChange={(e) =>
                  setForm({ ...form, instrument_type: e.target.value })
                }
                className="input"
              >
                {INSTRUMENT_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="מקור מימון">
              <select
                value={form.financing_source_type}
                onChange={(e) =>
                  setForm({ ...form, financing_source_type: e.target.value })
                }
                className="input"
              >
                {FINANCING_SOURCES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="שם בנק / מקור">
              <input
                type="text"
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                placeholder="לדוגמה: לאומי"
                className="input"
              />
            </Field>

            <Field label="תווית">
              <input
                type="text"
                value={form.facility_label}
                onChange={(e) =>
                  setForm({ ...form, facility_label: e.target.value })
                }
                placeholder="לדוגמה: הלוואה למימון רכב"
                className="input"
              />
            </Field>

            <Field label="יתרה קיימת (₪)">
              <input
                type="number"
                inputMode="numeric"
                value={form.amount_current}
                onChange={(e) =>
                  setForm({ ...form, amount_current: e.target.value })
                }
                className="input"
              />
            </Field>

            {isLoanInstrument(form.instrument_type) && (
              <Field label="סוג הלוואה">
                <select
                  value={form.loan_type}
                  onChange={(e) =>
                    setForm({ ...form, loan_type: e.target.value })
                  }
                  className="input"
                >
                  {LOAN_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="ריבית שנתית (%)">
              <input
                type="number"
                step="0.01"
                value={form.annual_rate_percent}
                onChange={(e) =>
                  setForm({ ...form, annual_rate_percent: e.target.value })
                }
                placeholder="6.25"
                className="input"
              />
            </Field>

            <Field label="תקופה (חודשים)">
              <input
                type="number"
                value={form.term_months}
                onChange={(e) =>
                  setForm({ ...form, term_months: e.target.value })
                }
                className="input"
              />
            </Field>

            <Field label="חלויות שנתיות (₪)">
              <input
                type="number"
                value={form.current_annual_maturities}
                onChange={(e) =>
                  setForm({
                    ...form,
                    current_annual_maturities: e.target.value,
                  })
                }
                className="input"
              />
            </Field>

            <Field label="הערות" className="md:col-span-3">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="input"
              />
            </Field>
          </div>

          {createFacility.isError && (
            <div className="flex items-start gap-2 p-3 mt-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>שגיאה בהוספה: {createFacility.error.message}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setForm(emptyForm);
              }}
              disabled={createFacility.isPending}
              className="btn-secondary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => createFacility.mutate(form)}
              disabled={!canSubmit}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {createFacility.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              שמור
            </button>
          </div>
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        {facilitiesQuery.isLoading ? (
          <div className="p-8 text-center text-slate-500">טוען…</div>
        ) : facilitiesQuery.isError ? (
          <div className="p-8 text-center text-red-600 text-sm">
            שגיאה בטעינה: {(facilitiesQuery.error as Error).message}
          </div>
        ) : (facilitiesQuery.data?.length ?? 0) === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            אין הלוואות מוזנות. לחץ על "הוסף הלוואה" כדי להתחיל.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <Th>חברה</Th>
                  <Th>מכשיר</Th>
                  <Th>בנק / מקור</Th>
                  <Th>תווית</Th>
                  <Th align="left">יתרה (₪)</Th>
                  <Th align="left">ריבית</Th>
                  <Th align="left">תקופה</Th>
                  <Th align="left">חלויות שנתיות (₪)</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(facilitiesQuery.data ?? []).map((f) => {
                  const company = companyMap.get(f.company_id);
                  return (
                    <tr key={f.id} className="hover:bg-slate-50">
                      <Td>{company?.short_label ?? '—'}</Td>
                      <Td>
                        <div>{labelOf(INSTRUMENT_TYPES, f.instrument_type)}</div>
                        {f.loan_type && (
                          <div className="text-xs text-slate-500">
                            {labelOf(LOAN_TYPES, f.loan_type)}
                          </div>
                        )}
                      </Td>
                      <Td>{f.bank_name ?? '—'}</Td>
                      <Td>{f.facility_label ?? '—'}</Td>
                      <Td align="left">{fmtMoney(f.amount_current)}</Td>
                      <Td align="left">{fmtRate(f.annual_rate)}</Td>
                      <Td align="left">
                        {f.term_months != null ? `${f.term_months} ח׳` : '—'}
                      </Td>
                      <Td align="left">
                        {fmtMoney(f.current_annual_maturities)}
                      </Td>
                      <Td align="left">
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('למחוק את ההלוואה?')) {
                              deleteFacility.mutate(f.id);
                            }
                          }}
                          disabled={deleteFacility.isPending}
                          className="text-slate-400 hover:text-red-600 transition disabled:opacity-50"
                          aria-label="מחק"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Th({
  children,
  align = 'right',
}: {
  children?: React.ReactNode;
  align?: 'right' | 'left';
}) {
  return (
    <th
      className={`px-4 py-3 font-medium ${
        align === 'left' ? 'text-left' : 'text-right'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'right',
}: {
  children?: React.ReactNode;
  align?: 'right' | 'left';
}) {
  return (
    <td
      className={`px-4 py-3 text-slate-700 ${
        align === 'left' ? 'text-left' : 'text-right'
      }`}
    >
      {children}
    </td>
  );
}
